import { ArrowRightLeft, History, RefreshCw, RotateCw } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import { useAuth } from "@/modules/auth/hooks/useAuth";
import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaEmpty } from "@/shared/components/feedback/ColocaEmpty";
import { ColocaSkeleton } from "@/shared/components/feedback/ColocaSkeleton";
import { ColocaPageHeader } from "@/shared/components/layout/ColocaPageHeader";
import { useAppShellBreadcrumb } from "@/shared/components/layout/useAppShellBreadcrumb";
import { NoAccessView } from "@/shared/components/NoAccessView";
import { TwoFactorActionModals } from "@/shared/components/overlays/TwoFactorActionModals";
import { BrebKeyDialog } from "@/shared/components/payments/breb-key/BrebKeyDialog";
import useTranslations from "@/shared/lang/useTranslations";

import { CryptoSwapOrganizationPicker } from "../components/shared/CryptoSwapOrganizationPicker";
import { ConfirmSwapDialog } from "../components/swap/ConfirmSwapDialog";
import { QuoteTicket } from "../components/swap/QuoteTicket";
import { SavedWalletsDialog } from "../components/swap/SavedWalletsDialog";
import { SwapCard } from "../components/swap/SwapCard";
import { VolumeDiscountBanner } from "../components/swap/VolumeDiscountBanner";
import { useCryptoSwapForm } from "../hooks/useCryptoSwapForm";
import { useCryptoSwapFormMessages } from "../hooks/useCryptoSwapFormMessages";
import { useCryptoSwapLabels } from "../hooks/useCryptoSwapLabels";
import { useCryptoSwapPairs } from "../hooks/useCryptoSwapPairs";
import { useOtcSavedWallets } from "../hooks/useOtcSavedWallets";
import { useOtcVolumeDiscount } from "../hooks/useOtcVolumeDiscount";
import { useSourceWalletBalance } from "../hooks/useSourceWalletBalance";
import { cryptoSwapErrorText } from "../utils/cryptoSwapErrors";
import { CRYPTO_SWAPS_HISTORY_PATH } from "../utils/cryptoSwapLinks";
import { buyUrlSearch, readBuyUrlState } from "../utils/cryptoSwapUrlState";

/** `/app/crypto-swaps` — buy USDT with the organization's COP balance, delivered to an external wallet. */
export default function CryptoSwapsPage() {
  const { t } = useTranslations("cryptoSwaps");
  const labels = useCryptoSwapLabels();
  const { can, isInternalUser } = useAuth();
  const isStaff = !!isInternalUser;
  const [searchParams, setSearchParams] = useSearchParams();
  // The amount lives in the URL so leaving to top up the wallet and coming back keeps it.
  const [initial] = useState(() => readBuyUrlState(searchParams));
  const [organizationId, setOrganizationId] = useState<string | null>(initial.organizationId);
  const [isBrebKeyOpen, setIsBrebKeyOpen] = useState(false);
  const [isSavedWalletsOpen, setIsSavedWalletsOpen] = useState(false);

  useAppShellBreadcrumb(
    useMemo(
      () => [{ label: t("page_breadcrumb_wallets"), to: "/app/wallets" }, { label: t("page_title") }],
      [t],
    ),
  );

  const canWrite = can("payments:write");
  const pairs = useCryptoSwapPairs(isStaff ? organizationId : null);
  const sendCurrency = pairs.pairs[0]?.send_currency ?? null;
  const organizationReady = !isStaff || !!organizationId;
  const balance = useSourceWalletBalance(sendCurrency, isStaff ? organizationId : null, organizationReady);
  const savedWallets = useOtcSavedWallets(
    isStaff ? organizationId : null,
    organizationReady && pairs.hasAccess,
  );
  const refreshSavedWallets = savedWallets.refresh;
  const onWalletSaved = useCallback(() => refreshSavedWallets(), [refreshSavedWallets]);

  const form = useCryptoSwapForm({
    pairs: pairs.pairs,
    canWrite,
    isStaff,
    organizationId: isStaff ? organizationId : null,
    availableBalance: balance.available,
    walletMissing: balance.missing,
    onWalletSaved,
    initial,
  });

  const urlSearch = buyUrlSearch({
    anchor: form.anchor,
    amount: form.amount,
    network: form.network,
    organizationId: isStaff ? organizationId : null,
  });
  useEffect(() => {
    if (urlSearch !== searchParams.toString()) setSearchParams(urlSearch, { replace: true });
  }, [urlSearch, searchParams, setSearchParams]);
  const { blockMessage, notice, quoteErrorText } = useCryptoSwapFormMessages(form);
  const volumeDiscount = useOtcVolumeDiscount({
    quote: form.quote,
    isPricing: form.isPricing,
    anchor: form.anchor,
    amount: form.amount,
    isStaff,
    organizationId,
  });
  const networkLabel = labels.network(form.network);

  const pickedWallet = form.destination.savedWalletId
    ? savedWallets.wallets.find((wallet) => wallet.id === form.destination.savedWalletId)
    : undefined;
  const walletNote = pickedWallet
    ? t("confirm_saved_wallet", { alias: pickedWallet.alias })
    : form.destination.save && form.destination.alias.trim()
      ? t("confirm_will_save_wallet", { alias: form.destination.alias.trim() })
      : null;

  if (!pairs.hasAccess) return <NoAccessView />;

  const organizationSlot = isStaff ? (
    <CryptoSwapOrganizationPicker
      id="crypto-swap-organization"
      mode="required"
      value={organizationId}
      onValueChange={setOrganizationId}
    />
  ) : undefined;

  return (
    <div className="flex flex-col gap-6 pb-16">
      <ColocaPageHeader
        title={t("page_title")}
        description={t("page_description")}
        actions={
          <ColocaButton variant="default" icon={History} render={<Link to={CRYPTO_SWAPS_HISTORY_PATH} />}>
            {t("page_history_link")}
          </ColocaButton>
        }
      />

      {pairs.isLoading && <CatalogSkeleton />}

      {!pairs.isLoading && pairs.hasError && (
        <ColocaEmpty
          icon={RefreshCw}
          title={t("pairs_load_failed")}
          description={t("error_service_unavailable")}
        >
          <ColocaButton variant="default" icon={RotateCw} onClick={pairs.refresh}>
            {t("pairs_retry")}
          </ColocaButton>
        </ColocaEmpty>
      )}

      {!pairs.isLoading && !pairs.hasError && pairs.isEmpty && (
        <ColocaEmpty icon={ArrowRightLeft} title={t("pairs_empty")} />
      )}

      {!pairs.isLoading && !pairs.hasError && !pairs.isEmpty && form.pair && (
        <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_380px] xl:grid-cols-[minmax(0,1fr)_400px]">
          <SwapCard
            form={form}
            organizationSlot={organizationSlot}
            disabled={!canWrite}
            onCreateBrebKey={canWrite ? () => setIsBrebKeyOpen(true) : undefined}
            savedWallets={savedWallets.wallets}
            canSaveWallet={canWrite}
            onManageSavedWallets={() => setIsSavedWalletsOpen(true)}
            volumeDiscount={
              volumeDiscount.view ? (
                <VolumeDiscountBanner
                  view={volumeDiscount.view}
                  pair={form.pair}
                  onApply={(suggestion) =>
                    form.applySuggestedAmount(volumeDiscount.applySuggestion(suggestion))
                  }
                />
              ) : undefined
            }
            balance={{
              available: balance.available,
              isLoading: balance.isLoading,
              hasError: balance.hasError,
              missing: balance.missing,
              needsOrganization: !organizationReady,
            }}
          />
          <QuoteTicket
            className="lg:sticky lg:top-6"
            pair={form.pair}
            quote={form.quote}
            isPricing={form.isPricing}
            clockOffsetMs={form.clockOffsetMs}
            networkLabel={networkLabel}
            notice={notice}
            blockMessage={blockMessage}
            canReview={form.block === null}
            onReview={form.openReview}
            onRequote={form.priceNow}
          />
        </div>
      )}

      <ConfirmSwapDialog
        open={form.reviewOpen}
        onClose={form.closeReview}
        pair={form.pair}
        quote={form.quote}
        isPricing={form.isPricing}
        address={form.destination.address.trim()}
        walletNote={walletNote}
        networkLabel={networkLabel}
        network={form.network}
        concept={form.destination.concept.trim()}
        clockOffsetMs={form.clockOffsetMs}
        block={form.block}
        blockMessage={blockMessage}
        quoteErrorMessage={quoteErrorText}
        acknowledged={form.acknowledged}
        onAcknowledgedChange={form.setAcknowledged}
        phase={form.submitPhase}
        errorKind={form.submitError?.kind ?? null}
        errorMessage={form.submitError ? cryptoSwapErrorText(form.submitError, t) : null}
        onConfirm={() => void form.submit()}
        onRequote={form.priceNow}
      />

      <TwoFactorActionModals
        controller={form.saveWalletTwoFactor}
        requiredDescription={t("saved_wallet_two_factor_required")}
        challengeTitle={t("saved_wallet_two_factor_title")}
        challengeDescription={t("saved_wallet_two_factor_description")}
      />

      <SavedWalletsDialog
        open={isSavedWalletsOpen}
        onClose={() => setIsSavedWalletsOpen(false)}
        wallets={savedWallets.wallets}
        canDelete={canWrite}
        onDelete={savedWallets.remove}
      />

      <BrebKeyDialog
        open={isBrebKeyOpen}
        organizationId={isStaff ? (organizationId ?? undefined) : undefined}
        onClose={() => {
          setIsBrebKeyOpen(false);
          pairs.refresh();
        }}
      />
    </div>
  );
}

function CatalogSkeleton() {
  return (
    <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_380px]">
      <ColocaSkeleton className="h-[560px] rounded-3xl" />
      <ColocaSkeleton className="h-[440px] rounded-3xl" />
    </div>
  );
}
